import { OktaAuth } from '@okta/okta-auth-js';
import { TokenManager, TOKEN_EXPIRY_BUFFER, isRedirectLocked, setLastRedirectTimestamp } from '../interface/TokenManager';

export class OktaTokenManager implements TokenManager {
    constructor(private oktaAuth: OktaAuth) {}

    async isTokenExpiring(): Promise<boolean> {
        const authState = this.oktaAuth.authStateManager.getAuthState();
        if (!authState?.isAuthenticated || !authState.accessToken) {
            return true;
        }

        const accessToken = authState.accessToken;
        const expirationTime = accessToken.expiresAt * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeUntilExpiration = expirationTime - currentTime;
        
        return timeUntilExpiration < TOKEN_EXPIRY_BUFFER;
    }

    async refreshToken(): Promise<string> {
        try {
            // Use Okta's token manager to renew the token
            const tokenResponse = await this.oktaAuth.tokenManager.renew('accessToken');
            
            // Check if we got a valid token response with accessToken property
            if (!tokenResponse || !('accessToken' in tokenResponse) || !(tokenResponse as any).accessToken) {
                throw new Error('Failed to renew access token');
            }
            
            // Update the auth state with the new token
            this.oktaAuth.authStateManager.updateAuthState();
            
            // Access accessToken property
            return (tokenResponse as any).accessToken;
        } catch (error) {
            console.error('[OktaTokenManager] Token renewal failed:', error);
            throw error;
        }
    }

    async getAccessToken(): Promise<string | null> {
        const authState = this.oktaAuth.authStateManager.getAuthState();
        return authState?.accessToken?.accessToken || null;
    }

    async loginRedirect(): Promise<void> {
        if (isRedirectLocked()) {
            console.error('Redirect locked. Please wait before trying again.');
            return;
        }

        setLastRedirectTimestamp();
        const currentUrl = window.location.href;
        const relativeUrl = currentUrl.replace(window.location.origin, '');
        
        await this.oktaAuth.signInWithRedirect({ originalUri: relativeUrl });
    }

    isRedirectLocked(): boolean {
        return isRedirectLocked();
    }

    lockRedirect(): void {
        setLastRedirectTimestamp();
    }
}
